import { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import { Spinner } from '@musleempay/ui';
import { AdminLayout } from './components/layout/AdminLayout.js';
import { DashboardLayout } from './components/layout/DashboardLayout.js';
import {
  RedirectIfAuthenticated,
  RequireAuth,
  RequirePlatformAdmin,
  RequireStore,
} from './components/layout/RequireAuth.js';
import { getAppUrl, getMerchantSlugFromHostname, isMarketingHost } from './lib/hostname.js';

/**
 * Route map.
 *
 * One bundle, three audiences, told apart by HOSTNAME (see
 * `src/lib/hostname.ts`), not by a shared URL path prefix:
 *
 *   * `<merchant-slug>.musleempay.com` — a merchant's storefront, rendered by
 *     `StorefrontApp` at root paths (`/`, `/page/:slug`, `/product/:slug`,
 *     `/checkout`, `/order/:orderNumber`) — no "/store/:slug" prefix, since
 *     the subdomain already says which merchant this is.
 *   * everything else (www.musleempay.com, the bare apex,
 *     app.musleempay.com, and local dev) — rendered by `MainApp`:
 *       - `/` — the public marketing page. A signed-in merchant who lands
 *         here is redirected to `/dashboard`.
 *       - `/dashboard/*` — the authenticated merchant dashboard.
 *       - `/login`, `/register`, … — unauthenticated account flows.
 *
 * Pages are lazily loaded so a shopper on a storefront never downloads the
 * dashboard bundle, and vice versa.
 */

// Marketing
const LandingPage = lazy(() => import('./pages/marketing/LandingPage.js'));

// Platform (Super Admin)
const AdminOverviewPage = lazy(() => import('./pages/admin/AdminOverviewPage.js'));
const AdminStoresPage = lazy(() => import('./pages/admin/AdminStoresPage.js'));
const AdminStoreDetailPage = lazy(() => import('./pages/admin/AdminStoreDetailPage.js'));
const AdminBillingPage = lazy(() => import('./pages/admin/AdminBillingPage.js'));
const AdminPlansPage = lazy(() => import('./pages/admin/AdminPlansPage.js'));
const AdminTeamPage = lazy(() => import('./pages/admin/AdminTeamPage.js'));
const AdminActivityPage = lazy(() => import('./pages/admin/AdminActivityPage.js'));

// Payment
const SandboxPaymentPage = lazy(() => import('./pages/payment/SandboxPaymentPage.js'));

// Account
const LoginPage = lazy(() => import('./pages/auth/LoginPage.js'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage.js'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage.js'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage.js'));
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage.js'));
const OnboardingPage = lazy(() => import('./pages/onboarding/OnboardingPage.js'));

// Dashboard
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage.js'));
const ProductListPage = lazy(() => import('./pages/products/ProductListPage.js'));
const ProductFormPage = lazy(() => import('./pages/products/ProductFormPage.js'));
const SalesPageListPage = lazy(() => import('./pages/sales-pages/SalesPageListPage.js'));
const SalesPageEditorPage = lazy(() => import('./pages/sales-pages/SalesPageEditorPage.js'));
const OrderListPage = lazy(() => import('./pages/orders/OrderListPage.js'));
const OrderDetailPage = lazy(() => import('./pages/orders/OrderDetailPage.js'));
const CustomerListPage = lazy(() => import('./pages/customers/CustomerListPage.js'));
const CustomerDetailPage = lazy(() => import('./pages/customers/CustomerDetailPage.js'));
const CouponListPage = lazy(() => import('./pages/coupons/CouponListPage.js'));
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage.js'));
const StaffPage = lazy(() => import('./pages/staff/StaffPage.js'));
const PaymentMethodsPage = lazy(() => import('./pages/payments/PaymentMethodsPage.js'));
const BillingPage = lazy(() => import('./pages/billing/BillingPage.js'));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage.js'));
const ShippingSettingsPage = lazy(() => import('./pages/settings/ShippingSettingsPage.js'));

// Storefront
const StorefrontHomePage = lazy(() => import('./pages/storefront/StorefrontHomePage.js'));
const StorefrontProductPage = lazy(() => import('./pages/storefront/StorefrontProductPage.js'));
const StorefrontSalesPage = lazy(() => import('./pages/storefront/StorefrontSalesPage.js'));
const StorefrontCheckoutPage = lazy(() => import('./pages/storefront/StorefrontCheckoutPage.js'));
const OrderConfirmationPage = lazy(() => import('./pages/storefront/OrderConfirmationPage.js'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage.js'));

function PageFallback(): JSX.Element {
  return (
    <div className="flex min-h-[50vh] items-center justify-center" role="status" aria-live="polite">
      <Spinner className="h-8 w-8 text-teal-700" />
      <span className="sr-only">Loading</span>
    </div>
  );
}

/** Rendered on a merchant subdomain — every path is relative to that one store. */
function StorefrontApp(): JSX.Element {
  return (
    <Routes>
      <Route path="/" element={<StorefrontHomePage />} />
      <Route path="/product/:productSlug" element={<StorefrontProductPage />} />
      <Route path="/page/:pageSlug" element={<StorefrontSalesPage />} />
      <Route path="/checkout" element={<StorefrontCheckoutPage />} />
      <Route path="/order/:orderNumber" element={<OrderConfirmationPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

/** Rendered everywhere else: marketing site, dashboard, account flows. */
function MainApp(): JSX.Element {
  return (
    <Routes>
      {/* Public marketing page — a signed-in merchant is sent to /dashboard. */}
      <Route
        path="/"
        element={
          <RedirectIfAuthenticated>
            <LandingPage />
          </RedirectIfAuthenticated>
        }
      />

      {/* The sandbox gateway's hosted page — a shopper arrives here from
          checkout, signed out, so it is public like the account flows. */}
      <Route path="/sandbox-payment" element={<SandboxPaymentPage />} />

      {/* Public account flows */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />

      {/* Onboarding sits outside the dashboard shell — it is its own flow. */}
      <Route
        path="/onboarding"
        element={
          <RequireAuth>
            <RequireStore>
              <OnboardingPage />
            </RequireStore>
          </RequireAuth>
        }
      />

      {/* Platform area — operators only. The guard is a courtesy; the API
          refuses every /api/v1/admin call from anyone else regardless. */}
      <Route
        element={
          <RequireAuth>
            <RequirePlatformAdmin>
              <AdminLayout />
            </RequirePlatformAdmin>
          </RequireAuth>
        }
      >
        <Route path="/admin" element={<AdminOverviewPage />} />
        <Route path="/admin/stores" element={<AdminStoresPage />} />
        <Route path="/admin/stores/:id" element={<AdminStoreDetailPage />} />
        <Route path="/admin/activity" element={<AdminActivityPage />} />
        <Route path="/admin/plans" element={<AdminPlansPage />} />
        <Route path="/admin/billing" element={<AdminBillingPage />} />
        <Route path="/admin/team" element={<AdminTeamPage />} />
      </Route>

      {/* Authenticated dashboard — a store's own pages. */}
      <Route
        element={
          <RequireAuth>
            <RequireStore>
              <DashboardLayout />
            </RequireStore>
          </RequireAuth>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/products" element={<ProductListPage />} />
        <Route path="/products/new" element={<ProductFormPage mode="create" />} />
        <Route path="/products/:id" element={<ProductFormPage mode="edit" />} />
        <Route path="/sales-pages" element={<SalesPageListPage />} />
        <Route path="/sales-pages/new" element={<SalesPageEditorPage mode="create" />} />
        <Route path="/sales-pages/:id" element={<SalesPageEditorPage mode="edit" />} />
        <Route path="/orders" element={<OrderListPage />} />
        <Route path="/orders/:id" element={<OrderDetailPage />} />
        <Route path="/customers" element={<CustomerListPage />} />
        <Route path="/customers/:id" element={<CustomerDetailPage />} />
        <Route path="/coupons" element={<CouponListPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/staff" element={<StaffPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/settings/payments" element={<PaymentMethodsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/shipping" element={<ShippingSettingsPage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

/**
 * On www and the bare apex only the landing page is served. Everything else
 * (sign-in, the dashboard, /admin, email links) belongs on the app host, so it is
 * sent there with its path intact — otherwise people sign in and work on the
 * marketing host and end up with two places for the same screens.
 */
function MoveToAppHost(): null {
  const { pathname, search, hash } = window.location;
  window.location.replace(getAppUrl(`${pathname}${search}${hash}`));
  return null;
}

export function App(): JSX.Element {
  // Decided once per full page load, which is the only time the hostname can
  // change — React Router's client-side navigation never crosses hosts.
  const isStorefront = getMerchantSlugFromHostname() !== null;

  if (isMarketingHost() && window.location.pathname !== '/') {
    return <MoveToAppHost />;
  }

  return (
    <Suspense fallback={<PageFallback />}>{isStorefront ? <StorefrontApp /> : <MainApp />}</Suspense>
  );
}
